"use client"

import * as React from "react"

import { LookupSelect, type SelectOption } from "@ibada/ui"
import { MobileFormRow, MobilePickerSheet } from "@/shared/components/mobile-form"

import { useLookup } from "@/shared/hooks/use-lookup"
import type { LookupType } from "@/shared/model/lookup-types"
import { TenantScopeContext } from "@/shared/model/tenant-scope-context"

export type DimensionPick = { id: string; label: string }

/**
 * A searchable async picker for one investigation dimension (account, fund, activity, user…). On desktop the
 * compact button IS the combobox trigger, so a click opens the same server-searched list the launcher has
 * always used, as a popover; on a phone it is a row that opens the shared picker sheet.
 */
export function DimensionPicker({
  type,
  label,
  icon,
  workspaceId,
  branchId,
  parishContext,
  onPick,
  mobile = false,
  doneLabel,
  emptyLabel,
}: {
  type: LookupType
  label: string
  icon?: React.ReactNode
  workspaceId?: string
  branchId?: string | null
  parishContext: boolean
  onPick: (result: DimensionPick) => void
  mobile?: boolean
  doneLabel?: string
  emptyLabel?: string
}) {
  const [query, setQuery] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const lookup = useLookup({
    type,
    query,
    limit: 15,
    workspaceId,
    branchId: parishContext ? undefined : branchId ?? undefined,
    context: parishContext ? TenantScopeContext.Parish : TenantScopeContext.Branch,
    enabled: Boolean(workspaceId),
  })

  const options = React.useMemo<SelectOption[]>(
    () => (lookup.data?.items ?? []).map((item) => ({ value: item.id, label: item.label })),
    [lookup.data?.items],
  )

  const pick = (id: string) => {
    const item = lookup.data?.items.find((candidate) => candidate.id === id)
    onPick({ id, label: item?.label ?? id })
  }

  if (mobile) {
    return (
      <>
        <MobileFormRow label={label} onPress={() => setOpen(true)} />
        <MobilePickerSheet
          open={open}
          onOpenChange={setOpen}
          title={label}
          doneLabel={doneLabel ?? ""}
          emptyLabel={emptyLabel ?? ""}
          searchPlaceholder={label}
          options={options.map((option) => ({ value: option.value, label: String(option.label) }))}
          onQueryChange={setQuery}
          loading={lookup.isFetching}
          onSelect={pick}
        />
      </>
    )
  }

  return (
    <LookupSelect
      value={undefined}
      onValueChange={(next) => {
        const id = Array.isArray(next) ? next[0] ?? "" : next
        if (id) pick(id)
      }}
      options={options}
      searchValue={query}
      onSearchValueChange={setQuery}
      searchPlaceholder={label}
      placeholder={
        <span className='flex min-w-0 items-center gap-2 text-foreground'>
          {icon ? <span className='shrink-0 text-primary [&_svg]:size-4'>{icon}</span> : null}
          <span className='truncate'>{label}</span>
        </span>
      }
      isLoading={lookup.isLoading}
      controlClassName='h-10 rounded-[11px] bg-card text-[13.5px] font-bold transition-colors hover:border-primary/30'
    />
  )
}
